import TableRow from "@mui/material/TableRow";
import { useContext, useState } from "react";
import { Product } from "../../../../../data/entities";
import { ROLE } from "../../../../../data/enums";
import { AuthenticationContext } from "../../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../../shared/components/IconButton";
import TextBox from "../../../../../shared/components/TextBox";
import SearchBarWithSuggestion from "../../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import CustomButton from "../../../../../shared/components/TitleRow/CustomButton";
import { api } from "../../../../../shared/hooks/api";
import { ContentWrapper, StyledNavLink } from "../../../../../shared/styled/TableStyles";
import DeleteIcon from "../../../../../shared/svg/DeleteIcon";
import { formatDate } from "../../../../../utils/dateUtils";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../../utils/format";
import { error } from "../../../../../utils/toast";
import { withDefaultCustomerPrice } from "../../../../../utils/customerProductPricing";
import { TableItemCounter, TableTitle } from "../../../../Material/single/MaterialList/Styled";
import { MaterialButtonRow } from "../../../../Provider/create/Content/MaterialList/Styled";
import AddProductModal from "./AddProductModal";

// Define table columns
const ProductTableColumns = [
    { text: "Mã sản phẩm", icon: <></> },
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Giá bán", icon: <></> },
    { text: "Ngày tạo", icon: <></> },
    { text: "", icon: <></> },
];

interface ProductRowProps {
    item: any;
    extraRowProps: any;
}

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const cannotSeePrice = [
        ROLE.STORAGE_KEEPER,
        ROLE.FIRST_STORAGE_KEEPER,
        ROLE.SECOND_STORAGE_KEEPER,
    ].includes(role);

    return (
        <TableRow>
            <StyledTableCell align="left">
                <StyledNavLink to={`/products/${item.id}`}>
                    {item.product_code || item.id}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left">{item.name}</StyledTableCell>
            {!cannotSeePrice && (
                <StyledTableCell align="left">
                    <TextBox
                        numeric
                        text={formatInternationalNumber(item.price ?? "")}
                        onChangeText={(value: string) => {
                            const n = parseInternationalToNumber(value, error);
                            if (!Number.isFinite(n)) return;
                            extraRowProps.onChangeProductPrice(item, n);
                        }}
                        disabled={!extraRowProps.canEdit}
                    />
                </StyledTableCell>
            )}
            <StyledTableCell align="left">
                {item.created_at ? formatDate(new Date(item.created_at)) : "—"}
            </StyledTableCell>
            <StyledTableCell align="left">
                <IconButton
                    onClick={() => extraRowProps.removeItem(item)}
                    disabled={!extraRowProps.canEdit}
                >
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

interface ProductListProps {
    products: any;
    mergeProducts: Function;
}

const ProductList = ({
                         products = [],
                         mergeProducts,
                     }: ProductListProps) => {
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [
        ROLE.MANAGER,
        ROLE.SALE_MANAGER,
        ROLE.SALE_STAFF,
    ].includes(role);

    const [{ data: allProductsData }, fetchAllProducts] = api.get("/products/");
    const allProducts = allProductsData?.results ?? [];
    const totalProducts = allProductsData?.count ?? 0;

    const handleSearchSelectProduct = (item: Product, isAdded: boolean) => {
        const alreadyIn = products.some((p: any) => p.id === item.id);
        if (isAdded && !alreadyIn) {
            mergeProducts({
                products: [...products, withDefaultCustomerPrice(item)],
            });
        } else if (!isAdded && alreadyIn) {
            mergeProducts({ products: products.filter((p: any) => p.id !== item.id) });
        }
    };

    const tableItems = products.length === 0 ? [] : products;

    const onRemoveProduct = (product: Product) => {
        const removedProducts = products.filter(
            (item: any) => item.id !== product.id,
        );
        mergeProducts({ products: removedProducts });
    };

    const onChangeProductPrice = (product: Product, price: number) => {
        const updatedProducts = products.map((item: any) => {
            if (item.id === product.id) return { ...item, price };
            return item;
        });
        mergeProducts({ products: updatedProducts });
    };

    return (
        <>
            {isProductModalOpen && (
                <AddProductModal
                    open={isProductModalOpen}
                    handleClose={() => setIsProductModalOpen(false)}
                    currentProducts={products}
                    onProductsUpdated={(updatedProducts) =>
                        mergeProducts({ products: updatedProducts })
                    }
                />
            )}
            <ContentWrapper>
                <TableTitle>
                    Danh sách sản phẩm
                    <TableItemCounter>
                        {" "}
                        ({products.length} sản phẩm)
                    </TableItemCounter>
                </TableTitle>

                {canEdit && (
                    <SearchBarWithSuggestion
                        menuList={allProducts}
                        selectedItems={products}
                        fetchItems={(variables: any) =>
                            fetchAllProducts({ ...variables, page_size: Math.max(totalProducts, 50) })
                        }
                        handleOnclickItem={handleSearchSelectProduct}
                        placeholder="Tìm kiếm sản phẩm..."
                    />
                )}

                <CustomTable
                    itemList={tableItems}
                    RowElement={ProductRow}
                    TableColumns={ProductTableColumns}
                    extraRowProps={{
                        removeItem: onRemoveProduct,
                        onChangeProductPrice,
                        canEdit,
                    }}
                />
                {tableItems.length === 0 && "Không có sản phẩm nào được chọn"}

                {canEdit && (
                    <MaterialButtonRow>
                        <CustomButton
                            title="Thêm Sản Phẩm"
                            onClick={() => setIsProductModalOpen(true)}
                            variant="contained"
                        />
                    </MaterialButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};

export default ProductList;
